import { useEffect, useRef, useState } from "react"

import { api, type AvisoNaTela } from "../api"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../componentes/DropdownMenu"
import * as Icones from "../componentes/Icones"
import { dataHora, inteiro } from "../formato"
import { BOTAO_DA_CASCA, MAIUSCULA } from "../lib/classes"
import { cn } from "../lib/cn"

const AVISOS_NA_PREVIA = 10

function textoDoBadge(naoLidos: number): string {
  return naoLidos > 99 ? "99+" : String(naoLidos)
}

function semPrefixo(assunto: string): string {
  return assunto.replace(/^\[ATHUS Contratos\]\s*/, "")
}

const avisarAsTelas = () => window.dispatchEvent(new CustomEvent("avisos-mudaram"))

export function SinoDeAvisos({
  entidadeId,
  aoVerTodos,
  aoAbrirContrato,
}: {
  entidadeId: number | null
  aoVerTodos: () => void
  aoAbrirContrato: (id: number) => void
}) {
  const [naoLidos, definirNaoLidos] = useState<number | null>(null)
  const [balancando, definirBalancando] = useState(false)
  const [recentes, definirRecentes] = useState<AvisoNaTela[] | null>(null)
  const anterior = useRef<number | null>(null)

  useEffect(() => {
    let vivo = true
    anterior.current = null
    const consultar = () => {
      api
        .avisos(true, 1)
        .then((corpo) => {
          if (!vivo) return
          const novo = corpo.meta.nao_lidos
          if (anterior.current !== null && novo > anterior.current) {
            definirBalancando(true)
          }
          anterior.current = novo
          definirNaoLidos(novo)
        })
        .catch(() => {
          if (vivo) definirNaoLidos(null)
        })
    }
    const seAVista = () => {
      if (document.visibilityState !== "hidden") consultar()
    }
    consultar()
    const relogio = window.setInterval(seAVista, 60 * 1000)
    window.addEventListener("avisos-mudaram", consultar)
    document.addEventListener("visibilitychange", seAVista)
    return () => {
      vivo = false
      window.clearInterval(relogio)
      window.removeEventListener("avisos-mudaram", consultar)
      document.removeEventListener("visibilitychange", seAVista)
    }
  }, [entidadeId])

  const contar = (novo: number) => {
    anterior.current = novo
    definirNaoLidos(novo)
  }

  const aoMudarAbertura = (aberto: boolean) => {
    if (!aberto) return
    definirRecentes(null)
    api
      .avisos(false, AVISOS_NA_PREVIA)
      .then((corpo) => {
        definirRecentes(corpo.dados)
        contar(corpo.meta.nao_lidos)
      })
      .catch(() => definirRecentes([]))
  }

  const abrir = (aviso: AvisoNaTela) => {
    if (aviso.lida_em === null) {
      api
        .marcarAvisoLido(aviso.id)
        .then((corpo) => {
          contar(corpo.meta.nao_lidos)
          avisarAsTelas()
        })
        .catch(() => undefined)
    }
    if (aviso.contrato_id !== null) aoAbrirContrato(aviso.contrato_id)
    else aoVerTodos()
  }

  const marcarTodos = () => {
    api
      .marcarTodosAvisosLidos()
      .then((corpo) => {
        contar(corpo.meta.nao_lidos)
        avisarAsTelas()
      })
      .catch(() => undefined)
  }

  const temNaoLidos = naoLidos !== null && naoLidos > 0
  const descricao = temNaoLidos
    ? `Meus avisos: ${inteiro(naoLidos ?? 0)} não lidos`
    : "Meus avisos"

  return (
    <DropdownMenu onOpenChange={aoMudarAbertura}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn(BOTAO_DA_CASCA, "relative")} title={descricao} aria-label={descricao}>
          <span
            className={balancando ? "inline-flex origin-top animate-[balancar-sino_0.6s_ease-in-out_1] motion-reduce:animate-none" : undefined}
            onAnimationEnd={() => definirBalancando(false)}
          >
            <Icones.Sino />
          </span>
          {temNaoLidos && (
            <span
              data-slot="sino-badge"
              className="pointer-events-none absolute -top-[7px] -right-[7px] box-border h-[18px] min-w-[18px] rounded-(--raio-pilula) bg-(--vermelho-vivo) px-[5px] text-center text-[0.6875rem] leading-[18px] font-semibold text-(--sobre-vermelho) tabular-nums"
              aria-hidden="true"
            >
              {textoDoBadge(naoLidos ?? 0)}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">Meus avisos</DropdownMenuLabel>
          {temNaoLidos && (
            <DropdownMenuItem
              className="h-7 shrink-0 px-2 text-xs font-medium text-primary"
              onSelect={(evento) => {
                evento.preventDefault()
                marcarTodos()
              }}
            >
              Marcar todos como lidos
            </DropdownMenuItem>
          )}
        </div>
        <DropdownMenuSeparator className="mx-0 my-0" />
        <div className="max-h-[22rem] overflow-y-auto p-1">
          {recentes === null ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">Carregando...</p>
          ) : recentes.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">Nenhum aviso</p>
          ) : (
            recentes.map((aviso) => {
              const naoLido = aviso.lida_em === null
              return (
                <DropdownMenuItem
                  key={aviso.id}
                  data-aviso={aviso.id}
                  data-nao-lido={String(naoLido)}
                  className="flex-col items-stretch gap-0.5 py-2"
                  onSelect={() => abrir(aviso)}
                >
                  <span className="flex items-start gap-2">
                    <span
                      className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", naoLido ? "bg-primary" : "bg-transparent")}
                      aria-hidden="true"
                    />
                    <span className={cn("text-sm leading-snug", naoLido ? "font-semibold text-foreground" : "text-muted-foreground", MAIUSCULA)}>
                      {semPrefixo(aviso.assunto)}
                    </span>
                  </span>
                  <span className="flex gap-2 pl-3.5 text-xs text-muted-foreground tabular-nums">
                    {aviso.contrato_numero !== null && !aviso.assunto.includes(aviso.contrato_numero) && (
                      <span>Contrato {aviso.contrato_numero}</span>
                    )}
                    <span>{dataHora(aviso.criado_em)}</span>
                  </span>
                </DropdownMenuItem>
              )
            })
          )}
        </div>
        <DropdownMenuSeparator className="mx-0 my-0" />
        <div className="p-1">
          <DropdownMenuItem className="justify-center text-sm font-medium" onSelect={aoVerTodos}>
            Ver todos os avisos
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
